import { useContext } from 'react'
import { JourneyStepType } from '../../../types'
import RadioInput from '../../../ui/form/RadioInput'
import TextInput from '../../../ui/form/TextInput'
import { DelayStepIcon } from '../../../ui/icons'
import { formatDate, formatDuration, snakeToTitle, toNaturalList } from '../../../utils'
import { PreferencesContext } from '../../../ui/PreferencesContext'
import { parse, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { MultiOptionField } from '../../../ui/form/MultiOptionField'
import { FieldOption } from '../../../ui/form/Field'
import VariableTextInput from '../../../ui/form/VariableTextInput'

interface DelayStepConfig {
    format: 'duration' | 'time' | 'date'
    minutes: number
    hours: number
    days: number
    time?: string
    date?: string
    exclusion_days?: number[]
}

export const getDayOptions = (t: (key: string) => string): FieldOption[] => [
    {
        key: 0,
        label: t('day_sun'),
    },
    {
        key: 1,
        label: t('day_mon'),
    },
    {
        key: 2,
        label: t('day_tue'),
    },
    {
        key: 3,
        label: t('day_wed'),
    },
    {
        key: 4,
        label: t('day_thu'),
    },
    {
        key: 5,
        label: t('day_fri'),
    },
    {
        key: 6,
        label: t('day_sat'),
    },
]

export const delayStep: JourneyStepType<DelayStepConfig> = {
    name: 'delay',
    icon: <DelayStepIcon />,
    category: 'delay',
    description: 'delay_desc',
    Describe({ value }) {
        const { t } = useTranslation()
        const [preferences] = useContext(PreferencesContext)
        const includedDays = getDayOptions(t).filter(item => !value.exclusion_days?.includes(Number(item.key)),
        )

        if (value.format === 'duration') {
            return (
                <>
                    {t('wait') + ' '}
                    <strong>
                        {formatDuration(preferences, {
                            days: value.days ?? 0,
                            hours: value.hours ?? 0,
                            minutes: value.minutes ?? 0,
                        }) || <>&#8211;</>}
                    </strong>
                </>
            )
        }
        if (value.format === 'time') {
            const parsed = parse(value.time ?? '', 'HH:mm', new Date())
            return (
                <>
                    {t('wait_until') + ' '}
                    <strong>
                        {isNaN(parsed.getTime()) ? '--:--' : formatDate(preferences, parsed, 'p')}
                    </strong>
                    {(includedDays.length < 7) && <>
                        {' ' + t('on') + ' '}
                        {toNaturalList(includedDays.map(day => day.label))}
                    </>}
                </>
            )
        }
        if (value.format === 'date') {
            const parsed = parseISO(value.date ?? '')
            return (
                <>
                    {t('wait_until') + ' '}
                    <strong>
                        {isNaN(parsed.getTime())
                            ? value.date?.includes('{{')
                                ? <><br />{value.date}</>
                                : <>&#8211;</>
                            : formatDate(preferences, parsed, 'PP')}
                    </strong>
                </>
            )
        }
        return null
    },
    newData: async () => ({
        minutes: 0,
        hours: 0,
        days: 0,
        format: 'duration',
    }),
    Edit({
        onChange,
        value,
        disabled,
    }) {
        const { t } = useTranslation()
        return (
            <>
                <RadioInput
                    label={t('type')}
                    options={[
                        { key: 'duration', label: t('for_duration') },
                        { key: 'time', label: t('until_time') },
                        { key: 'date', label: t('until_date') },
                    ]}
                    value={value.format}
                    onChange={format => onChange({ ...value, format }) }
                    disabled={disabled}
                />
                { value.format === 'duration'
                    && ['days', 'hours', 'minutes'].map(name => (
                        <TextInput
                            key={name}
                            name={name}
                            label={snakeToTitle(name)}
                            type="number"
                            min={0}
                            value={value[name as keyof Pick<DelayStepConfig, 'days' | 'hours' | 'minutes'>] ?? 0}
                            onChange={n => onChange({ ...value, [name]: n })}
                            disabled={disabled}
                        />
                    ))
                }
                { value.format === 'time'
                    && <div style={{ maxWidth: 400 }}>
                        <TextInput
                            name="time"
                            label={t('time')}
                            type="time"
                            subtitle={t('delay_time_desc')}
                            value={value.time ?? ''}
                            onChange={time => onChange({ ...value, time })}
                            disabled={disabled}
                        />
                        <MultiOptionField
                            options={getDayOptions(t)}
                            value={value.exclusion_days ?? []}
                            onChange={days => onChange({ ...value, exclusion_days: days })}
                            label={t('delay_exclusion_dates')}
                            subtitle={t('delay_exclusion_dates_desc')}
                            max={6}
                            disabled={disabled}
                        />
                    </div>
                }
                { value.format === 'date'
                    && <VariableTextInput
                        name="date"
                        label={t('date')}
                        subtitle={t('delay_date_desc')}
                        placeholder="YYYY-MM-DD"
                        value={value.date ?? ''}
                        onChange={date => onChange({ ...value, date })}
                        disabled={disabled}
                    />
                }
            </>
        )
    },
}
